import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When any value in this array changes, the boundary auto-resets if it's
   * currently in the error state. Pass `[location.pathname]` to reset on
   * route change — fixes the "stuck error" trap where a transient error
   * (e.g. brief 502 during a deploy) leaves the app in the fallback state
   * even after the underlying problem has resolved.
   *
   * Use shallow-compared values; deep equality is intentionally not done
   * (would defeat memoization in the parent).
   */
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;     // Sentry event ID, if captured
  isStaleChunk: boolean;      // detected stale lazy-chunk error
  copied: boolean;            // "Copy details" feedback
  autoReloadIn: number | null;// stale-chunk auto-reload countdown (seconds)
}

// Detect lazy-import failures from a Vite/CRA deploy that replaced asset
// hashes while the user's tab held the old HTML in memory. The signature
// varies by browser and bundler — match on multiple known patterns.
function isStaleChunkError(error: Error | null | undefined): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();
  return (
    name === 'chunkloaderror' ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    // Vite-specific: dynamic import resolution returns a 404 when the
    // bundle hash has rolled. The thrown TypeError surfaces as one of:
    msg.includes('dynamically imported module') ||
    // Safari-specific message for the same root cause
    msg.includes('module script failed')
  );
}

// One-shot guard: only auto-reload once per session. If the reload doesn't
// fix the error (i.e. it's a real bug, not a stale chunk), don't loop.
const AUTO_RELOAD_KEY = 'tw_error_boundary_auto_reload';
function hasAutoReloadedThisSession(): boolean {
  try {
    return sessionStorage.getItem(AUTO_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}
function markAutoReloaded(): void {
  try {
    sessionStorage.setItem(AUTO_RELOAD_KEY, '1');
  } catch {
    /* sessionStorage disabled — accept the small risk of loop */
  }
}

/**
 * React Error Boundary — catches rendering errors in child components
 * and displays a fallback UI instead of crashing the entire app.
 *
 * Behaviour:
 *  - Stale lazy-chunk errors (deploy caused the user's cached chunk URLs
 *    to disappear) trigger a one-shot auto-reload after a 3s countdown.
 *    The session-level guard prevents reload loops on real bugs.
 *  - Other errors show actionable details: the error message, a Sentry
 *    event ID for support reference, and a "Copy details" button.
 *  - In production (Sentry DSN configured at index.js init), all errors
 *    are shipped to Sentry with the React component stack as extra
 *    context. captureException is a no-op when Sentry isn't initialised.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private autoReloadTimer: ReturnType<typeof setInterval> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      eventId: null,
      isStaleChunk: false,
      copied: false,
      autoReloadIn: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, isStaleChunk: isStaleChunkError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    const eventId = Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack || 'unknown' },
      },
      tags: {
        is_stale_chunk: String(isStaleChunkError(error)),
      },
    });
    this.setState({ eventId: eventId || null });

    // Stale chunk path — start the auto-reload countdown unless we've
    // already reloaded once this session (loop guard).
    if (isStaleChunkError(error) && !hasAutoReloadedThisSession()) {
      this.startAutoReload();
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (!this.state.hasError) return;
    const prevKeys = prevProps.resetKeys ?? [];
    const nextKeys = this.props.resetKeys ?? [];
    if (prevKeys.length !== nextKeys.length) {
      this.resetState();
      return;
    }
    for (let i = 0; i < nextKeys.length; i += 1) {
      if (!Object.is(prevKeys[i], nextKeys[i])) {
        this.resetState();
        return;
      }
    }
  }

  componentWillUnmount(): void {
    if (this.autoReloadTimer) clearInterval(this.autoReloadTimer);
  }

  private resetState = (): void => {
    if (this.autoReloadTimer) {
      clearInterval(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }
    this.setState({
      hasError: false,
      error: null,
      eventId: null,
      isStaleChunk: false,
      copied: false,
      autoReloadIn: null,
    });
  };

  private startAutoReload = (): void => {
    if (this.autoReloadTimer) return;
    this.setState({ autoReloadIn: 3 });
    this.autoReloadTimer = setInterval(() => {
      this.setState((prev) => {
        const next = (prev.autoReloadIn ?? 0) - 1;
        if (next <= 0) {
          markAutoReloaded();
          window.location.reload();
          return { autoReloadIn: 0 };
        }
        return { autoReloadIn: next };
      });
    }, 1000);
  };

  private cancelAutoReload = (): void => {
    if (this.autoReloadTimer) {
      clearInterval(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }
    this.setState({ autoReloadIn: null });
  };

  private handleRetry = (): void => {
    this.resetState();
  };

  private handleHardReload = (): void => {
    markAutoReloaded();
    window.location.reload();
  };

  private handleCopyDetails = async (): Promise<void> => {
    const { error, eventId } = this.state;
    const details = [
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      eventId ? `Sentry Event ID: ${eventId}` : null,
      error ? `Error: ${error.name}: ${error.message}` : null,
      error?.stack ? `\nStack:\n${error.stack}` : null,
    ].filter(Boolean).join('\n');

    try {
      await navigator.clipboard.writeText(details);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Fallback for older browsers / non-secure contexts
      window.prompt('Copy these details for support:', details);
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { error, eventId, isStaleChunk, copied, autoReloadIn } = this.state;

    // ── Stale-chunk variant: friendlier copy + auto-reload countdown ──
    if (isStaleChunk) {
      return (
        <div role="alert" style={containerStyle}>
          <h2 style={{ color: 'var(--dark)', marginBottom: '0.5rem' }}>
            App updated — reloading…
          </h2>
          <p style={{ color: 'var(--gray)', marginBottom: '1.5rem', maxWidth: '520px' }}>
            A new version of TenderWright was deployed while this tab was open.
            We need to reload to pick up the new code.
            {autoReloadIn !== null && autoReloadIn > 0 && (
              <> Auto-reloading in <strong>{autoReloadIn}s</strong>…</>
            )}
          </p>
          <div style={buttonRowStyle}>
            <button onClick={this.handleHardReload} style={primaryButtonStyle}>
              Reload now
            </button>
            {autoReloadIn !== null && autoReloadIn > 0 && (
              <button onClick={this.cancelAutoReload} style={secondaryButtonStyle}>
                Cancel auto-reload
              </button>
            )}
          </div>
        </div>
      );
    }

    // ── General error variant: actionable details + copyable Sentry ID ──
    return (
      <div role="alert" style={containerStyle}>
        <h2 style={{ color: 'var(--dark)', marginBottom: '0.5rem' }}>
          Something went wrong
        </h2>
        <p style={{ color: 'var(--gray)', marginBottom: '1rem', maxWidth: '520px' }}>
          An unexpected error occurred in this section. The most common
          fix is a hard reload (the bundle may be stale). If that doesn't
          work, copy the details below and email{' '}
          <a href="mailto:reza.moghaddam.nz@gmail.com" style={{ color: 'var(--primary, #1A56DB)' }}>
            reza.moghaddam.nz@gmail.com
          </a>.
        </p>
        {error && (
          <p style={{
            color: 'var(--danger, #c0392b)',
            background: '#fef2f2',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: '0.85rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            maxWidth: 520,
            wordBreak: 'break-word',
            marginBottom: '1rem',
          }}>
            {error.name}: {error.message}
          </p>
        )}
        {eventId && (
          <p style={{ color: 'var(--gray)', fontSize: '0.78rem', marginBottom: '1rem' }}>
            Support reference: <code>{eventId}</code>
          </p>
        )}
        <div style={buttonRowStyle}>
          <button onClick={this.handleHardReload} style={primaryButtonStyle}>
            Reload page
          </button>
          <button onClick={this.handleRetry} style={secondaryButtonStyle}>
            Try again
          </button>
          <button onClick={this.handleCopyDetails} style={secondaryButtonStyle}>
            {copied ? 'Copied ✓' : 'Copy details'}
          </button>
          <button
            onClick={() => (window.location.href = '/dashboard')}
            style={secondaryButtonStyle}
          >
            Go to Dashboard
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && error && (
          <details
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              maxWidth: '700px',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <summary style={{ cursor: 'pointer', color: 'var(--danger)' }}>
              Stack trace (development only)
            </summary>
            <pre style={{
              marginTop: '0.5rem',
              fontSize: '0.72rem',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              color: 'var(--danger)',
            }}>
              {error.stack || '(no stack)'}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  padding: '2rem',
  textAlign: 'center',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  backgroundColor: 'var(--primary, #1A56DB)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  backgroundColor: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.875rem',
};

export default ErrorBoundary;
